import { Box, ButtonBase, Stack, Typography } from '@mui/material'
import { useMemo } from 'react'
import { YALO_RADIUS, YALO_SEMANTIC } from '@commerce/tokens'
import {
  WEEKDAY_INITIALS,
  WEEKDAY_NAMES,
  calendarMonths,
  deliveryOptionLabel,
  type CalendarMonth,
} from '../checkout/deliveryOptions'

/**
 * Visible to a screen reader, not to a sighted user.
 *
 * The lengths are strings on purpose: in MUI's `sx`, a bare `width: 1` means
 * 100%, not 1px, which made every one of these "invisible" boxes the size of
 * the viewport and gave the whole screen a phantom scroll in both axes.
 */
const srOnly = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0,
} as const

interface Props {
  /** The date tokens the client system offered, exactly as it sent them. */
  options: readonly string[]
  /** The chosen token, exactly as offered. */
  selected: string | null
  onSelect: (token: string) => void
}

const MonthGrid = ({
  month,
  selected,
  onSelect,
}: {
  month: CalendarMonth
  selected: string | null
  onSelect: (token: string) => void
}) => (
  <Box>
    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
      <Typography variant="overline" color="text.secondary">
        Fecha de entrega
      </Typography>
      <Typography variant="body2" fontWeight={600}>
        {month.label}
      </Typography>
    </Stack>

    <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
      <Box component="caption" sx={srOnly}>
        {`Fechas de entrega disponibles en ${month.label}`}
      </Box>
      <thead>
        <tr>
          {WEEKDAY_INITIALS.map((initial, index) => (
            <Box
              component="th"
              scope="col"
              key={WEEKDAY_NAMES[index]}
              sx={{
                pb: 1,
                fontSize: 11,
                fontWeight: 600,
                color: YALO_SEMANTIC.fg3,
              }}
            >
              {/* The initial is decoration; the column's real name is the
                  weekday, and a screen reader announces it per cell. */}
              <Box component="span" aria-hidden>
                {initial}
              </Box>
              <Box component="span" sx={srOnly}>
                {WEEKDAY_NAMES[index]}
              </Box>
            </Box>
          ))}
        </tr>
      </thead>
      <tbody>
        {month.weeks.map((week, weekIndex) => (
          // A week has no identity but its position, and a month never
          // reorders — the first cell's day would be `null` for week one.
          <tr key={`week-${weekIndex}`}>
            {week.map(({ day, token }, dayIndex) => (
              <Box
                component="td"
                key={day ?? `blank-${weekIndex}-${dayIndex}`}
                sx={{ p: 0.25, textAlign: 'center' }}
              >
                {day !== null && (
                  <ButtonBase
                    onClick={() => token !== null && onSelect(token)}
                    disabled={token === null}
                    aria-pressed={token !== null && selected === token}
                    aria-label={
                      token !== null
                        ? deliveryOptionLabel(token)
                        : `${day} de ${month.label.toLowerCase()}, no disponible`
                    }
                    sx={(theme) => {
                      const isSelected = token !== null && selected === token
                      const available = token !== null
                      return {
                        width: '100%',
                        aspectRatio: '1',
                        borderRadius: `${YALO_RADIUS.sm + 2}px`,
                        fontSize: 13,
                        fontWeight: available || isSelected ? 600 : 400,
                        bgcolor: isSelected
                          ? theme.palette.primary.main
                          : available
                            ? theme.palette.primary.light
                            : 'transparent',
                        color: isSelected
                          ? theme.palette.primary.contrastText
                          : available
                            ? YALO_SEMANTIC.fg1
                            : YALO_SEMANTIC.fg4,
                        '&.Mui-disabled': { color: YALO_SEMANTIC.fg4 },
                        '&.Mui-focusVisible': {
                          outline: `2px solid ${theme.palette.primary.main}`,
                          outlineOffset: 2,
                        },
                      }
                    }}
                  >
                    {day}
                  </ButtonBase>
                )}
              </Box>
            ))}
          </tr>
        ))}
      </tbody>
    </Box>
  </Box>
)

/**
 * The delivery-date picker, drawn over what the client system offered.
 *
 * A real `<table>` per month the offer touches, because that is what this is:
 * seven columns of weekdays and a row per week, and a table gives the column
 * headers for free. Each offered day is a real `<button>` whose accessible
 * name is the full date — a grid of bare numbers is unusable to anyone who
 * cannot see the column it sits under.
 *
 * A day is selectable exactly when it is in `options`; everything else is
 * `disabled`, skipped by Tab and refused by a click. Selecting reports the
 * offered token **verbatim** — the server validates the write against a
 * freshly computed list with strict string equality, so the token must never
 * be parsed and re-serialised on its way back.
 */
export const DeliveryCalendar = ({ options, selected, onSelect }: Props) => {
  const months = useMemo(() => calendarMonths(options), [options])

  return (
    <Box
      sx={{
        bgcolor: YALO_SEMANTIC.bgSurface,
        border: `1px solid ${YALO_SEMANTIC.borderSubtle}`,
        borderRadius: `${YALO_RADIUS.lg}px`,
        p: 2,
      }}
    >
      {months.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No hay fechas de entrega disponibles por ahora.
        </Typography>
      ) : (
        <Stack spacing={2.5}>
          {months.map((month) => (
            <MonthGrid key={month.label} month={month} selected={selected} onSelect={onSelect} />
          ))}
        </Stack>
      )}
    </Box>
  )
}
